import * as React from "react";

import { isIdleCallbackEnabled, uniqueDropdownId } from "@1js/acui-common";
import { AppContext } from "@1js/acui-context";
import type { MenuDefinition } from "@1js/acui-menu";
import { DefaultMenuAnchorContext } from "@1js/acui-menu";
import { getShadowRenderMenuValue } from "@1js/acui-shadow-rendering";
import type { AppTooltipHost } from "@1js/acui-tooltip";

import { setTooltipsOnMenuItems } from "../../../utilities";
import type { AppFlyoutAnchorProps } from "./AppFlyoutAnchor.types";
import { useGetMenuProps } from "./useGetMenuProps";
import type { AppSplitButtonProps } from "../../AppSplitButton/next/AppSplitButton.types";

export const useShadowRenderedMenu = (
  props: AppFlyoutAnchorProps | AppSplitButtonProps,
  typeId: string
) => {
  const initiatedShadowRendering = React.useRef(false);
  const tooltipHostMap = React.useRef<Map<string, AppTooltipHost>>(new Map());
  const [menuHasBeenOpened, setMenuHasBeenOpened] = React.useState(false);
  const [isShadowRendering, setIsShadowRendering] = React.useState(false);
  const context = React.useContext(AppContext);
  const flyoutDropdownId = React.useRef(uniqueDropdownId(props, props.id));

  const {
    id,
    location,
    shadowRenderMenu,
    disabled,
    onExecute,
    deferredMenuDefinition,
    persistMenu,
  } = props;

  /**
   * If a user opens the menu by clicking, keyboarding, through keytips
   * or through the API, the first thing we need to do is set shadow rendering
   * to false.
   */
  const resetShadowRenderingAfterUserAction = React.useCallback(() => {
    if (isShadowRendering) {
      setIsShadowRendering(false);
    }
  }, [isShadowRendering, setIsShadowRendering]);

  const onSubMenuReady = React.useCallback(
    (menu: MenuDefinition | undefined, flyoutMenuId?: string) => {
      if (flyoutMenuId) {
        tooltipHostMap.current = setTooltipsOnMenuItems(
          menu,
          flyoutMenuId + "MenuId",
          tooltipHostMap.current,
          context.setTooltipElement,
          context.updateTooltipsHidden,
          true /** perform on next frame */
        );
      }
    },
    [context]
  );

  const onCalloutReady = React.useCallback(
    (menu: MenuDefinition | undefined) => {
      if (tooltipHostMap.current.size === 0) {
        tooltipHostMap.current = setTooltipsOnMenuItems(
          menu,
          flyoutDropdownId.current,
          tooltipHostMap.current,
          context.setTooltipElement,
          context.updateTooltipsHidden,
          true /** perform on next frame */
        );
      }
    },
    [flyoutDropdownId, context]
  );

  const onMenuDismissed = React.useCallback(() => {
    tooltipHostMap.current.forEach((tooltipHost) => {
      tooltipHost.unregisterTooltipHostActions();
    });
    tooltipHostMap.current.clear();
  }, []);

  const onMenuOpened = React.useCallback(() => {
    if (!menuHasBeenOpened) {
      setMenuHasBeenOpened(true);
    }
    resetShadowRenderingAfterUserAction();
  }, [
    menuHasBeenOpened,
    setMenuHasBeenOpened,
    resetShadowRenderingAfterUserAction,
  ]);

  /**
   * This method is called on componentDidMount and componentDidUpdate.
   * If shadow rendering has already been initiated this is no-op.
   * If not, it tries to initiate it.
   * The reason why we have to call this multiple times on update is because
   * we do not want to initiate shadow rendering when the control is disabled.
   */
  const tryInitiateShadowRendering = React.useCallback(() => {
    if (
      !initiatedShadowRendering.current &&
      getShadowRenderMenuValue(shadowRenderMenu) &&
      isIdleCallbackEnabled() &&
      !disabled &&
      !menuHasBeenOpened &&
      !props.menuDefinition?.shouldAutoOpen
    ) {
      initiatedShadowRendering.current = true;

      window.requestIdleCallback(
        () => {
          onExecute?.({ id, location, isShadowRendering: true });
        },
        undefined /*options*/,
        // @ts-ignore Bug:5821196 - [TS 4.5.2 upgrade OOUI] Fix requestIdleCallback across OOUI
        `${typeId}-${id}-ShadowMenuOpen`
      );

      window.requestIdleCallback(
        () => {
          if (!menuHasBeenOpened) {
            setMenuHasBeenOpened(true);
            setIsShadowRendering(true);
          }
        },
        undefined /*options*/,
        // @ts-ignore Bug:5821196 - [TS 4.5.2 upgrade OOUI] Fix requestIdleCallback across OOUI
        `${typeId}-${id}-StartShadowRendering`
      );
    }
  }, [
    id,
    typeId,
    location,
    disabled,
    menuHasBeenOpened,
    shadowRenderMenu,
    onExecute,
    props.menuDefinition?.shouldAutoOpen,
  ]);

  // On every update
  tryInitiateShadowRendering();

  const getMenuProps = useGetMenuProps(
    props,
    flyoutDropdownId.current,
    onMenuOpened,
    onSubMenuReady,
    onCalloutReady,
    onMenuDismissed
  );

  const menuAnchorContextValue = React.useMemo(
    () =>
      deferredMenuDefinition
        ? {
            deferredMenuDefinition,
            getMenuPropsFromMenuDefinition: getMenuProps,
          }
        : DefaultMenuAnchorContext,
    [getMenuProps, deferredMenuDefinition]
  );

  return {
    isShadowRendering,
    persistMenu: persistMenu && menuHasBeenOpened,
    menuAnchorContextValue,
    getMenuProps,
  };
};
